/**
 * OpenPropertyIntakeForm renderer — Runner wrapper around PropertyIntakeForm.
 * Wires save/cancel back to the runner's advance flow and pulls the target
 * property_id + hydration data from captured_data.
 *
 * Auto-fill (iter303ag): when `params.auto_fill_from === "client_agent"` the
 * client's name / phone / email are copied into the initial form values AND
 * pushed to the property on save via `extraPatch`. Also mirrors the top-level
 * radio `captured_data.agent_same_as_owner` (yes/no string) onto the property
 * as a boolean so downstream logic + the Property detail page stay in sync.
 *
 * Hydration (iter303ag-fix2):
 *   • Reads the per-action slot `captured_data.property_intake_forms[action.id]`
 *     first (falls back to the legacy single-slot for pre-fix runs), so
 *     Back-nav to any of the 4 intake actions restores exactly what was
 *     entered — no cross-action clobber.
 *   • Fetches `GET /roof-details/{property_id}` on mount and merges its
 *     fields into the initial form values. This means roof-entity fields
 *     (roof_type / material_type / roof_brand / roof_colour / roof_age /
 *     roof_pitch / roof_stories) rehydrate from the source of truth on
 *     Back, even if the operator edited the roof in the property pop-out.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PropertyIntakeForm from '../intake/PropertyIntakeForm';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function buildClientAgentAutoFill(clientRec, clientForm) {
  const c = { ...clientRec, ...clientForm };
  const name = [c.name_first, c.name_last].filter(Boolean).join(' ').trim();
  const phone = c.phone_mobile || c.phone_home || c.phone_work || '';
  const email = c.email || '';
  const company = c.is_company ? (c.company_name || '') : '';
  const out = {};
  if (name)    out.agent_name = name;
  if (phone)   out.agent_phone = phone;
  if (email)   out.agent_email = email;
  if (company) out.agent_company = company;
  return out;
}

export default function OpenPropertyIntakeForm({ action, capturedData, value, onChange, onNext, onBack, onCancel, canBack,
                                                  statusKey, taskId, onTemplateChanged }) {
  const params = action.params || {};
  const propertyId = capturedData?.property_id;
  const propertyRecord = capturedData?.property_record || {};
  // Per-action draft slot (falls back to the legacy single-slot for old runs)
  const perActionForms = capturedData?.property_intake_forms || {};
  const draftForm = perActionForms[action.id] || capturedData?.property_intake_form || {};
  const clientRecord = capturedData?.client_record || {};
  const clientForm = capturedData?.client_intake_form || {};
  const navigate = useNavigate();

  // Fetch the primary roof_details record on mount so roof-entity fields
  // (roof_type, material_type, roof_brand, roof_colour, roof_age,
  // roof_pitch, roof_stories) can hydrate on Back navigation. Roof
  // fields live on `roof_details`, not on the property doc, so a fresh
  // GET here is the only way to preload them.
  const [roofRecord, setRoofRecord] = useState({});
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    api.get(`/roof-details/${propertyId}`)
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        // Prefer the "Main Roof" record; otherwise the first row.
        const primary = rows.find(r => (r.description || '').toLowerCase() === 'main roof') || rows[0] || {};
        setRoofRecord(primary);
      })
      .catch(() => setRoofRecord({}));
    return () => { cancelled = true; };
  }, [propertyId]);

  // Auto-fill values when the action requests them (e.g. "Client is the
  // agent" → copy client's name/phone/email onto the agent fields).
  const autoFill = useMemo(() => {
    if (params.auto_fill_from === 'client_agent') {
      return buildClientAgentAutoFill(clientRecord, clientForm);
    }
    return {};
  }, [params.auto_fill_from, clientRecord, clientForm]);

  // Sync the top-level Yes/No radio for `agent_same_as_owner` (written by
  // RadioChoice into captured_data) onto the property as a proper boolean.
  const agentOwnerFlag = capturedData?.agent_same_as_owner;
  const extraPatch = useMemo(() => {
    const patch = { ...autoFill };
    if (agentOwnerFlag === 'yes' || agentOwnerFlag === true) patch.agent_same_as_owner = true;
    else if (agentOwnerFlag === 'no' || agentOwnerFlag === false) patch.agent_same_as_owner = false;
    return patch;
  }, [autoFill, agentOwnerFlag]);

  // Layer order (earlier keys are overridden by later ones):
  //   propertyRecord → roofRecord → autoFill → draftForm
  // Draft wins so an operator's in-flight edits during this session
  // survive over stale record data. Roof fields come from the source
  // of truth (roof_details), not the property doc.
  // Memoized on a signature so the object reference is stable across
  // re-renders when contents don't actually change (prevents the child
  // PropertyIntakeForm's useEffect merge from firing needlessly).
  const initialSig = JSON.stringify({ pr: propertyRecord, rr: roofRecord, af: autoFill, df: draftForm });
  const initial = useMemo(() => ({
    ...propertyRecord, ...roofRecord, ...autoFill, ...draftForm,
  }), [initialSig]);

  const autoSaveDraft = (draft) => onChange({ ...draft, form: draft });

  const handleSave = (result) => {
    // Persist property_intake_form + updated property_record via WorkflowRunnerPage.advance
    const payload = {
      property_id: result.property_id,
      property_record: result.property_record,
      property_intake_form: result.form,
    };
    onChange(payload);
    onNext(payload);
  };

  const handleCancel = () => {
    if (onCancel) return onCancel();
    if (canBack) return onBack();
    navigate('/workflow/start');
  };

  const editCtx = (statusKey && taskId && action?.id) ? {
    statusKey, taskId, actionId: action.id,
    headerTitle: params.header_title,
    headerDescription: params.header_description,
    onTemplateChanged,
  } : null;

  return (
    <div>
      <PropertyIntakeForm
        propertyId={propertyId}
        initial={initial}
        prompts={params.prompts || ''}
        onSave={handleSave}
        onCancel={handleCancel}
        autoSaveDraft={autoSaveDraft}
        editCtx={editCtx}
        extraPatch={extraPatch}
        hideAnswered={!!params.hide_answered}
      />
    </div>
  );
}
